import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Badge, PopOver } from "../../components/atoms";
import { getFirstFailureStep, getFormattedDate } from "../../helpers";
import { Table, Pagination, EmptyLayout } from "../../components/molecules";
import { chevronLeftIcon, deployIcon } from "../../assets/icons";
import {
  selectSelectedApp,
  selectApplicationFilters,
  selectApplicationDeploymentsTrack,
} from "./index";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { DEFAULT_LIMIT, DEPLOY_STATUSES, ONE } from "../../constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { actions } from "./applications.reducers";

const { setSelectedTab } = breadCrumbActions;
const { setSelectedAppId, setApplicationFilters } = actions;
const { FETCH_APPLICATIONS_DEPLOYMENTS_TRACK } = SPINNER_NAMES;
const { SUCCESS, IN_PROGRESS } = DEPLOY_STATUSES;

const NOT_APPLICABLE = "N/A";

const ApplicationDeploymentsTrack = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { appId } = useParams();
  const navigateToPrevPage = () => navigate(-1);
  const selectedApp = useSelector(selectSelectedApp);
  const { deploymentStatus, count } = useSelector(
    selectApplicationDeploymentsTrack
  );
  const filters = useSelector(selectApplicationFilters);
  const [pageCount, setPageCount] = useState(ONE);
  const [applicationDeploymentCount, setApplicationDeploymentCount] =
    useState();

  useEffect(() => {
    if (appId) dispatch(setSelectedAppId(appId));
  }, [appId, dispatch]);

  useEffect(() => {
    if (count) setApplicationDeploymentCount(count);
  }, [count]);

  useEffect(() => {
    if (applicationDeploymentCount)
      setPageCount(Math.ceil(applicationDeploymentCount / DEFAULT_LIMIT));
  }, [applicationDeploymentCount]);

  useEffect(() => {
    if (selectedApp) dispatch(setSelectedTab(selectedApp));
  }, [dispatch, selectedApp]);

  const handlePagination = useCallback(
    (value) =>
      dispatch(setApplicationFilters({ ...filters, page: value + ONE })),
    [dispatch, filters]
  );

  const dateFormatter = ({ updatedAt }) =>
    updatedAt ? getFormattedDate(updatedAt) : NOT_APPLICABLE;

  const statusFormatter = ({ finalStatus, status }) => {
    const failure = getFirstFailureStep(status);
    return (
      <div className="flex items-center justify-between">
        <Badge value={finalStatus} size="xs" />
        {![SUCCESS, IN_PROGRESS].includes(finalStatus) && (
          <PopOver
            options={failure}
            classes="right-6 -bottom-3"
            icon="info"
            type="error"
          />
        )}
      </div>
    );
  };

  const versionFormatter = ({ version }) => (
    <Badge value={version} size="xs" color="primary" shouldFormat= {false} />
  );

  const startTime = ({ startTime }) =>
    startTime ? getFormattedDate(startTime) : NOT_APPLICABLE;
  const completeTime = ({ completeTime }) =>
    completeTime ? getFormattedDate(completeTime) : NOT_APPLICABLE;
  const getRepoName = ({ githubRepoName }) => githubRepoName || NOT_APPLICABLE;

  const columns = [
    {
      text: "Repo Name",
      formatter: getRepoName,
    },
    {
      text: "Deployed At",
      formatter: dateFormatter,
    },
    {
      text: "Start At",
      formatter: startTime,
    },
    {
      text: "Complete At",
      formatter: completeTime,
    },
    {
      text: "Version",
      formatter: versionFormatter,
    },
    {
      text: "Status",
      formatter: statusFormatter,
    },
  ];

  return (
    <div className="content scrollbar bg-gray-900 overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title> Deployments | TavaJS</title>
      </Helmet>
      <div className="flex-row xs:flex-row flex mb-4 gap-4">
        <button
          className="bg-gray-800 h-10 px-2 rounded-md hover:bg-gray-700/60"
          onClick={() => navigateToPrevPage()}
        >
          <img src={chevronLeftIcon} width="24" alt="chevron-left icon" />
        </button>
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_APPLICATIONS_DEPLOYMENTS_TRACK}>
          {count ? (
            <Table data={deploymentStatus} columns={columns} />
          ) : (
            <EmptyLayout
              title="No Deployment Logs Found"
              subTitle="You can get started by deployed again"
              shouldShowButton={false}
              image={deployIcon}
            />
          )}
        </Spinner>
      </div>
      <Pagination
        handlePagination={handlePagination}
        pagesCount={pageCount}
        activePage={filters.page - ONE}
        count={applicationDeploymentCount}
      />
    </div>
  );
};

export default ApplicationDeploymentsTrack;
